/**
 * Plural forms for counted nouns in the UI (STORY-289).
 *
 * Counters used to glue a number to one frozen word — «1 уникальных
 * инструментов», «2 рецепт», «(1 запросов)». A dictionary entry for a counted
 * noun is now a PluralForms object and the counter picks its form with
 * Intl.PluralRules: Russian uses one / few / many (1 запрос, 2 запроса,
 * 5 запросов) and `other` for fractions (2,5 запроса); English uses one /
 * other (1 request, 2 requests).
 */

export interface PluralForms {
  one: string;
  few?: string;
  many?: string;
  other: string;
}

const rulesByLanguage = new Map<string, Intl.PluralRules>();

function rulesFor(language: string): Intl.PluralRules {
  let rules = rulesByLanguage.get(language);
  if (!rules) {
    rules = new Intl.PluralRules(language === 'en' ? 'en' : 'ru');
    rulesByLanguage.set(language, rules);
  }
  return rules;
}

/** The form of `forms` that `count` selects in `language`. */
export function pluralize(language: string, count: number, forms: PluralForms): string {
  const category = rulesFor(language).select(count);
  if (category === 'one') return forms.one;
  if (category === 'few') return forms.few ?? forms.other;
  if (category === 'many') return forms.many ?? forms.other;
  return forms.other;
}

/**
 * «<count> <noun>», e.g. «2 вызова», «1 unique tool». `display` replaces the
 * printed number (a localized or abbreviated one such as «2.2K») while the
 * form is still chosen from the raw `count`.
 */
export function formatCount(
  language: string,
  count: number,
  forms: PluralForms,
  display?: string | number,
): string {
  return `${display ?? count} ${pluralize(language, count, forms)}`;
}

/**
 * A number grouped the way the UI language writes it — «1 000» in Russian,
 * «1,000» in English — not the way the browser locale would (STORY-329).
 */
export function formatNumber(language: string, value: number): string {
  return value.toLocaleString(language === 'en' ? 'en-US' : 'ru-RU');
}
